import React, { useState, useEffect, useMemo } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { fetchMyPlots, fetchDocuments, uploadDocument, fetchDocumentDownloadUrl } from "../api/api";

const CATEGORIES = [
  { value: "sale_deed", label: "Sale Deed" },
  { value: "plot_plan", label: "Plot Plan" },
  { value: "encumbrance", label: "Encumbrance Certificate" },
  { value: "water_cert", label: "Water Certificate" },
  { value: "electricity", label: "Electricity" },
  { value: "horticulture_report", label: "Horticulture Report" },
  { value: "noc_certificate", label: "NoC Certificate" },
  { value: "other", label: "Other" },
];

function categoryLabel(category) {
  return CATEGORIES.find((c) => c.value === category)?.label || category;
}

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function Documents() {

  const { role } = useAuth();
  const isAdmin = role === "admin";

  const [plots, setPlots] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [category, setCategory] = useState("");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const [downloadingId, setDownloadingId] = useState(null);

  const plot = plots[0] || null;

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const requests = [fetchDocuments()];
      if (!isAdmin) requests.push(fetchMyPlots());
      const [docList, plotList] = await Promise.all(requests);
      setDocuments(docList);
      if (plotList) setPlots(plotList);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [isAdmin]);

  const filteredDocuments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((d) => {
      if (categoryFilter && d.category !== categoryFilter) return false;
      if (q) {
        const haystack = `${d.label} ${d.plot_number || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [documents, search, categoryFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!plot) {
      setFormError("You don't have a registered plot yet.");
      return;
    }
    if (!category) {
      setFormError("Please select a document type.");
      return;
    }
    if (!label.trim()) {
      setFormError("Please give the document a label.");
      return;
    }
    if (!file) {
      setFormError("Please choose a file to upload.");
      return;
    }

    setSubmitting(true);
    try {
      await uploadDocument(plot.id, category, label.trim(), file);
      setCategory("");
      setLabel("");
      setFile(null);
      setFormSuccess("Document uploaded.");
      await load();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (documentId) => {
    setDownloadingId(documentId);
    try {
      const url = await fetchDocumentDownloadUrl(documentId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(errorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div className="document-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 style={{ marginTop: 0, marginBottom: 0 }}>
            {isAdmin ? "Document Management" : "My Documents"}
          </h1>
        </div>

        {loadError && <p className="login-error" style={{ color: "red" }}>{loadError}</p>}

        <div className="document-search" style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder={isAdmin ? "Search by plot or document name" : "Search by document name"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: "220px" }}
          />

          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">All Types</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        {loading && <p>Loading documents…</p>}

        {!loading && (
          <div className="document-table-card">

            <table className="document-table">

              <thead>
                <tr>
                  <th>Document</th>
                  <th>Type</th>
                  {isAdmin && <th>Plot</th>}
                  {isAdmin && <th>Owner</th>}
                  <th>Uploaded</th>
                  <th>Download</th>
                </tr>
              </thead>

              <tbody>

                {filteredDocuments.length === 0 && (
                  <tr><td colSpan={isAdmin ? 6 : 4} style={{ padding: "15px" }}>No documents found.</td></tr>
                )}

                {filteredDocuments.map((d) => (
                  <tr key={d.id}>
                    <td>📄 {d.label}</td>
                    <td><span className="doc-type">{categoryLabel(d.category)}</span></td>
                    {isAdmin && <td>{d.plot_number || "—"}</td>}
                    {isAdmin && <td>{d.owner_name || "—"}</td>}
                    <td>{new Date(d.created_at).toLocaleDateString()}</td>
                    <td>
                      <button className="toggle-btn" onClick={() => handleDownload(d.id)} disabled={downloadingId === d.id}>
                        {downloadingId === d.id ? "…" : "⬇️ Download"}
                      </button>
                    </td>
                  </tr>
                ))}

              </tbody>

            </table>

            {!isAdmin && (
              <div className="upload-box">

                <div className="upload-icon">⬆</div>
                <p>Upload a document for your plot</p>
                <small>PDF or image files</small>

                {!plot && <p style={{ color: "#991b1b" }}>You don't have a registered plot yet.</p>}
                {formError && <p className="login-error" style={{ color: "red" }}>{formError}</p>}
                {formSuccess && <p style={{ color: "#166534", fontWeight: 600 }}>{formSuccess}</p>}

                <form onSubmit={handleSubmit}>
                  <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">Select Document Type</option>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>

                  <input
                    type="text"
                    placeholder="Label (e.g. Sale Deed 2024)"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                  />

                  <input
                    type="file"
                    accept=".pdf,image/*"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />

                  <button type="submit" className="upload-btn" disabled={submitting || !plot}>
                    {submitting ? "Uploading…" : "Upload Document"}
                  </button>
                </form>

              </div>
            )}

          </div>
        )}

      </div>

    </div>

  );

}

export default Documents;
